import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { SpinnerGap } from '@phosphor-icons/react';

const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export default function ComparativesPage() {
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState([]);
  const [annual, setAnnual] = useState([]);
  const [quarters, setQuarters] = useState({});
  const [tab, setTab] = useState('mes');
  // Ano/mes seleccionado na tab "Mês". Default = mês corrente.
  const today = useMemo(() => new Date(), []);
  const [monthPick, setMonthPick] = useState({ ano: today.getFullYear(), mes: today.getMonth() + 1 });
  const [monthData, setMonthData] = useState(null); // { key: [] } cache
  const [monthLoading, setMonthLoading] = useState(false);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    api.post('/comparativos/init').then(r => {
      const d = r.data || {};
      setCurrentMonth(d.currentMonth?.data || []);
      setAnnual(d.annual?.data || []);
      setQuarters({ 1: d.q1?.data || [], 2: d.q2?.data || [], 3: d.q3?.data || [], 4: d.q4?.data || [] });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Sempre que o utilizador escolhe um mês/ano diferente do actual, vai
  // ao backend buscar a faturação desse mês. O mês/ano actual é servido
  // directamente pelo cache do /init.
  const isCurrent = monthPick.ano === today.getFullYear() && monthPick.mes === today.getMonth() + 1;
  const monthKey = `${monthPick.ano}-${monthPick.mes}`;
  useEffect(() => {
    if (tab !== 'mes' || isCurrent) return;
    if (monthData && monthData[monthKey]) return;
    setMonthLoading(true);
    api.post('/comparativos/monthBilling', { ano: monthPick.ano, mes: monthPick.mes })
      .then(r => {
        setMonthData(prev => ({ ...(prev || {}), [monthKey]: r.data?.data || [] }));
      })
      .catch(() => {
        setMonthData(prev => ({ ...(prev || {}), [monthKey]: [] }));
      })
      .finally(() => setMonthLoading(false));
  }, [tab, monthKey, isCurrent, monthData, monthPick.ano, monthPick.mes]);

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  const tabs = [
    { key: 'mes', label: 'Mês' },
    { key: 'anual', label: 'Acumulado Anual' },
    { key: 'q1', label: '1º Tri' },
    { key: 'q2', label: '2º Tri' },
    { key: 'q3', label: '3º Tri' },
    { key: 'q4', label: '4º Tri' },
  ];

  const getData = () => {
    if (tab === 'mes') {
      if (isCurrent) return currentMonth;
      return (monthData && monthData[monthKey]) || [];
    }
    if (tab === 'anual') return [...annual].sort((a, b) => (b.Total || 0) - (a.Total || 0));
    if (tab === 'q1') return quarters[1] || [];
    if (tab === 'q2') return quarters[2] || [];
    if (tab === 'q3') return quarters[3] || [];
    if (tab === 'q4') return quarters[4] || [];
    return [];
  };

  const data = getData();
  const isAnnual = tab === 'anual';
  const isQuarter = ['q1','q2','q3','q4'].includes(tab);
  const isMonth = tab === 'mes';

  // Cor do texto da linha, com regras distintas por separador:
  //  • Mês:       < 150 € vermelho · 150–299,99 € amarelo · >= 300 € verde
  //  • Trimestre: 1º ano < 900 € amarelo, senão verde · 2º+ ano < 1800 € amarelo, senão verde
  //  • Anual:     1º ano < 7000 € amarelo, senão verde · 2º+ ano < 14000 € amarelo, senão verde
  const YELLOW = '#CA8A04', GREEN = '#16A34A', RED = '#DC2626';
  const rowColor = (r, total) => {
    const v = Number(total) || 0;
    if (isMonth) {
      if (v < 150) return RED;
      if (v < 300) return YELLOW;
      return GREEN;
    }
    if (isQuarter) {
      const limite = r.PrimeiroAno ? 900 : 1800;
      return v < limite ? YELLOW : GREEN;
    }
    if (isAnnual) {
      const limite = r.PrimeiroAno ? 7000 : 14000;
      return v < limite ? YELLOW : GREEN;
    }
    return '#111827';
  };

  const getBadges = (total) => {
    if (!total || total <= 0) return { stars: 0, elite: false };
    if (isMonth) {
      const stars = Math.floor(total / 1500);
      return { stars, elite: total >= 8500 };
    }
    if (isQuarter) {
      const stars = Math.floor(total / 4500);
      return { stars, elite: total >= 25000 };
    }
    return { stars: 0, elite: false };
  };

  return (
    <div data-testid="comparatives-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Comparativos</h1>
        <p className="text-sm text-[#6B7280] mt-1">Comparativo de faturação mensal, trimestral e anual da rede</p>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#E5E7EB] overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`comparatives-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab===t.key?'border-[#0F1829] text-[#0F1829]':'border-transparent text-[#6B7280]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {isMonth && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="comparatives-month-picker">
          <label className="text-xs font-medium text-[#6B7280] uppercase tracking-[0.15em]">Mês:</label>
          <select
            data-testid="comparatives-month-select"
            value={monthPick.mes}
            onChange={(e) => setMonthPick(p => ({ ...p, mes: Number(e.target.value) }))}
            className="border border-[#E5E7EB] rounded-md px-2.5 py-1.5 text-sm bg-white focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none">
            {MESES_PT.map((nome, i) => (
              <option key={i + 1} value={i + 1}>{nome}</option>
            ))}
          </select>
          <select
            data-testid="comparatives-year-select"
            value={monthPick.ano}
            onChange={(e) => setMonthPick(p => ({ ...p, ano: Number(e.target.value) }))}
            className="border border-[#E5E7EB] rounded-md px-2.5 py-1.5 text-sm bg-white focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none">
            {Array.from({ length: 5 }, (_, k) => today.getFullYear() - k).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {!isCurrent && (
            <button
              type="button"
              data-testid="comparatives-month-reset"
              onClick={() => setMonthPick({ ano: today.getFullYear(), mes: today.getMonth() + 1 })}
              className="text-xs text-[#0F1829] hover:underline">
              ↺ Mês actual
            </button>
          )}
          <span className="ml-auto text-xs font-medium text-[#111827] bg-[#F3F4F6] px-2.5 py-1.5 rounded">
            {MESES_PT[monthPick.mes - 1]} {monthPick.ano}
          </span>
        </div>
      )}

      <p className="text-sm text-[#6B7280] mb-3">
        {isMonth && monthLoading ? 'A carregar…' : `${data.length} consultores`}
      </p>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Nome</th>
              {isAnnual ? (
                <>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">1º Tri</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">2º Tri</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">3º Tri</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">4º Tri</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Total</th>
                </>
              ) : (
                <>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Fat. Pessoal</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Fat. Clientes</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Total</th>
                </>
              )}
            </tr></thead>
            <tbody>
              {data.slice(0, 50).map((r, i) => {
                const total = isAnnual ? r.Total : r.FacturacaoTotal;
                const { stars, elite } = getBadges(total);
                const textColor = rowColor(r, total);
                return (
                <tr key={r.ID_Cliente ?? r.NomeCompleto ?? `row-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" style={{ color: textColor }} data-testid={`comparatives-row-${i}`}>
                  <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                    {r.NomeCompleto}
                    {stars > 0 && <span className="ml-2 text-xs font-bold text-amber-500">{stars}<span className="text-amber-400">&#9733;</span></span>}
                    {elite && <span className="ml-2 text-[9px] uppercase tracking-[0.1em] font-bold text-white bg-gradient-to-r from-[#978D78] to-[#E040FB] px-1.5 py-0.5 rounded">Elite Prime</span>}
                  </td>
                  {isAnnual ? (
                    <>
                      <td className="px-4 py-3 text-sm font-mono text-right">{fmt(r.FacturacaoTrimestre1)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-right">{fmt(r.FacturacaoTrimestre2)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-right">{fmt(r.FacturacaoTrimestre3)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-right">{fmt(r.FacturacaoTrimestre4)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-right font-bold">{fmt(r.Total)}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm font-mono text-right">{fmt(r.FacturacaoPessoal)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-right">{fmt(r.FacturacaoClientes)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-right font-bold">{fmt(r.FacturacaoTotal)}</td>
                    </>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem dados de faturação</div>}
      </div>
    </div>
  );
}
